if (!customElements.get('hover-gallery-carousel-slider')) {
  class HoverGalleryCarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));

      if (!this.track || !this.slides.length || !this.dots.length) {
        return;
      }

      this.activeIndex = this.slides.length > 1 ? 1 : 0;
      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnResize = this.onResize.bind(this);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index, 10) || 0;
          this.scrollToSlide(index, true);
        });
      });

      this.track.addEventListener('scroll', this.boundOnScroll, { passive: true });
      window.addEventListener('resize', this.boundOnResize);
      this.setActiveDot(this.activeIndex);
      window.requestAnimationFrame(() => {
        this.scrollToSlide(this.activeIndex, false);
      });

      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const block = event.target.closest('[data-slide]');
          if (!block) {
            return;
          }
          const index = this.slides.indexOf(block);
          if (index >= 0) {
            this.scrollToSlide(index, false);
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.track && this.boundOnScroll) {
        this.track.removeEventListener('scroll', this.boundOnScroll);
      }
      if (this.boundOnResize) {
        window.removeEventListener('resize', this.boundOnResize);
      }
    }

    onResize() {
      this.scrollToSlide(this.activeIndex, false);
    }

    onScroll() {
      if (this.scrollRAF) {
        return;
      }
      this.scrollRAF = window.requestAnimationFrame(() => {
        this.scrollRAF = null;
        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let nearestIndex = 0;
        let nearestDistance = Infinity;

        this.slides.forEach((slide, index) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(trackCenter - slideCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        if (nearestIndex !== this.activeIndex) {
          this.setActiveDot(nearestIndex);
        }
      });
    }

    scrollToSlide(index, smooth) {
      const target = this.slides[index];
      if (!target) {
        return;
      }
      const left = target.offsetLeft - (this.track.clientWidth - target.clientWidth) / 2;
      this.track.scrollTo({
        left,
        behavior: smooth ? 'smooth' : 'auto'
      });
      this.setActiveDot(index);
    }

    setActiveDot(index) {
      this.activeIndex = index;
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
  }

  customElements.define('hover-gallery-carousel-slider', HoverGalleryCarouselSlider);
}

if (!customElements.get('hover-gallery-carousel-desktop')) {
  class HoverGalleryCarouselDesktop extends HTMLElement {
    connectedCallback() {
      this.gallery = this.querySelector('.hover-gallery-carousel__gallery');
      this.cards = Array.from(this.querySelectorAll('.hover-gallery-card'));
      this.desktopMedia = window.matchMedia('(min-width: 768px)');

      if (!this.gallery || !this.cards.length) {
        return;
      }

      this.defaultCard = this.cards[0];
      this.activeCard = this.defaultCard;
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnMediaChange = this.onMediaChange.bind(this);

      this.cards.forEach((card) => {
        card.addEventListener('mouseenter', () => {
          if (!this.desktopMedia.matches) {
            return;
          }
          this.activeCard = card;
          this.setExpandedCard(card);
        });
        card.addEventListener('focusin', () => {
          if (!this.desktopMedia.matches) {
            return;
          }
          this.activeCard = card;
          this.setExpandedCard(card);
        });
      });

      window.addEventListener('resize', this.boundOnResize);
      this.desktopMedia.addEventListener('change', this.boundOnMediaChange);

      if (this.desktopMedia.matches) {
        this.setExpandedCard(this.defaultCard);
        this.syncHeightByFirstCard();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundOnResize);
      this.desktopMedia.removeEventListener('change', this.boundOnMediaChange);
    }

    onResize() {
      this.syncHeightByFirstCard();
    }

    onMediaChange() {
      if (!this.desktopMedia.matches) {
        this.cards.forEach((card) => card.classList.remove('is-expanded'));
        this.gallery.style.removeProperty('--hgc-gallery-height');
      } else {
        this.setExpandedCard(this.activeCard || this.defaultCard);
        this.syncHeightByFirstCard();
      }
    }

    setExpandedCard(card) {
      if (!card) {
        return;
      }
      this.cards.forEach((item) => {
        item.classList.toggle('is-expanded', item === card);
      });
    }

    syncHeightByFirstCard() {
      if (!this.desktopMedia.matches || !this.gallery || !this.defaultCard) {
        if (this.gallery) {
          this.gallery.style.removeProperty('--hgc-gallery-height');
        }
        return;
      }

      const ratio = parseFloat(this.defaultCard.dataset.aspectRatio) || 1.7778;
      const collapsedWidthValue = getComputedStyle(this.defaultCard).getPropertyValue('--hgc-collapsed-width').trim();
      const collapsedWidth = parseFloat(collapsedWidthValue) || 88;
      const gap = parseFloat(getComputedStyle(this.gallery).columnGap) || parseFloat(getComputedStyle(this.gallery).gap) || 0;
      const collapsedCount = Math.max(this.cards.length - 1, 0);
      const expandedWidth = this.gallery.clientWidth - collapsedCount * collapsedWidth - collapsedCount * gap;
      if (!expandedWidth || expandedWidth <= 0 || !ratio) {
        return;
      }

      const height = Math.max(240, expandedWidth / ratio);
      this.gallery.style.setProperty('--hgc-gallery-height', `${height}px`);
    }
  }

  customElements.define('hover-gallery-carousel-desktop', HoverGalleryCarouselDesktop);
}
